"use client";

import { cn } from "@/lib/utils";
import * as React from "react";

export interface ProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  value?: number;
  max?: number;
  indicatorClassName?: string;
}

const Progress = React.forwardRef<HTMLDivElement, ProgressProps>(
  ({ className, value = 0, max = 100, indicatorClassName, ...props }, ref) => {
    const percentage = Math.min(Math.max((value / max) * 100, 0), 100);

    return (
      <div
        ref={ref}
        className={cn(
          "h-2 w-full overflow-hidden rounded-full bg-muted",
          className,
        )}
        {...props}
      >
        <div
          className={cn(
            "h-full bg-primary transition-all duration-300 ease-in-out rounded-full",
            indicatorClassName,
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>
    );
  },
);

Progress.displayName = "Progress";

export interface DotStepperProps extends React.HTMLAttributes<HTMLDivElement> {
  current: number;
  total: number;
  size?: "sm" | "md" | "lg";
}

const DotStepper = React.forwardRef<HTMLDivElement, DotStepperProps>(
  ({ className, current, total, size = "md", ...props }, ref) => {
    const completedElements = current * 2 - 1;
    const elements = [];
    const dotSizes = {
      sm: "w-2.5 h-2.5",
      md: "size-3",
      lg: "size-4",
    };

    const barHeights = {
      sm: "h-[2px]",
      md: "h-[3px]",
      lg: "h-[4px]",
    };

    for (let i = 0; i < total; i++) {
      const elementIndex = i * 2;
      const dotCompleted = elementIndex < completedElements;
      elements.push(
        <div
          key={`dot-${i}`}
          className={cn(
            "shrink-0 rounded-sm",
            dotSizes[size],
            dotCompleted ? "bg-status-success" : "bg-muted-foreground/40",
          )}
        />,
      );

      if (i < total - 1) {
        const barIndex = i * 2 + 1;
        const barCompleted = barIndex < completedElements;
        elements.push(
          <div
            key={`bar-${i}`}
            className={cn(
              "basis-0 grow min-h-px min-w-px shrink-0",
              barHeights[size],
              barCompleted ? "bg-status-success" : "bg-muted-foreground/40",
            )}
          />,
        );
      }
    }

    return (
      <div
        ref={ref}
        className={cn(
          "relative flex items-center justify-center w-full",
          className,
        )}
        {...props}
      >
        {elements}
      </div>
    );
  },
);

DotStepper.displayName = "DotStepper";

export { Progress, DotStepper };
